import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PicklistService {

  constructor() { }
  printPickList(batchData:any):any{
    const groupedMedications: {
      [medicationName: string]: {
        prescriptionIds: string[];
        quantity: number;
        medicationId: string;
        /** MPS lines: packs to pick, per pack size, e.g. "4 × 28, 1 × 14". Null otherwise. */
        packs: string | null;
      };
    } = {};
    const packsBySize: { [medicationName: string]: Map<number, number> } = {};
    if(batchData != null){
      batchData.prescriptions.forEach((prescription: any) => {
        prescription.medications.forEach((medication: any) => {
          if (medication.fullPacks && medication.packSize) {
            const sizes = (packsBySize[medication.medicationName] ??= new Map<number, number>());
            sizes.set(medication.packSize, (sizes.get(medication.packSize) ?? 0) + medication.fullPacks);
          }
          if (groupedMedications[medication.medicationName]) {
            groupedMedications[medication.medicationName].prescriptionIds.push(
              prescription.rxId
            );
            groupedMedications[medication.medicationName].quantity += 1;
          } else {
            groupedMedications[medication.medicationName] = {
              prescriptionIds: [prescription.rxId],
              quantity: 1,
              medicationId: medication.medicationId,
              packs: null,
            };
          }
        });
      });
      for (const [name, sizes] of Object.entries(packsBySize)) {
        groupedMedications[name].packs = [...sizes]
          .sort((a, b) => b[0] - a[0])
          .map(([size, count]) => `${count} × ${size}`)
          .join(', ');
      }
    }
    return groupedMedications;
  }

  /** The pick list as a self-printing page: one row per stock item. */
  pickListHtml(batchData: any, today: string, baseHref: string): string {
    const grouped = this.printPickList(batchData);
    const rows = Object.entries<any>(grouped).map(([name, g]) => `
          <tr>
            <td>${escapeHtml(name)}</td>
            <td>Ending -${escapeHtml(String(g.prescriptionIds?.[0] ?? '').slice(-8))}</td>
            <td>${g.quantity}</td>
            <td>${escapeHtml(g.packs ?? '')}</td>
            <td></td>
          </tr>`).join('');
    const logo = (file: string) => escapeHtml(new URL(`assets/images/${file}`, baseHref).href);
    return `<html>
  <head>
    <title>Batch Pick List</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif;
        padding: 20px;
        margin: 0;
        color: #000;
      }

      .print-container {
        width: 100%;
      }

      .print-top-bar {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 0;
      }

      .print-date {
        font-size: 13px;
        color: #555;
      }

      .print-logos {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .print-logo {
        height: 36px;
      }

      .print-title {
        margin: 8px 0 14px 0;
        font-size: 20px;
        font-weight: 700;
        letter-spacing: 0.2px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
      }

      thead th {
        text-align: left;
        padding: 6px 6px;
        border-bottom: 1px solid #e0e0e0;
        font-weight: 700;
        font-size: 14px;
      }

      tbody td {
        padding: 6px;
        border-bottom: 1px solid #e0e0e0;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      .col-stock { width: 40%; }
      .col-prescription { width: 20%; }
      .col-qty { width: 10%; }
      .col-packs { width: 15%; }
      .col-picked { width: 15%; }
    </style>
  </head>
  <body onload="window.print(); window.close();">
    <div class="print-container">
      <div class="print-top-bar">
        <div class="print-date">${escapeHtml(today)}</div>
        <div class="print-logos">
          <img src="${logo('mclernons.png')}" class="print-logo" />
          <img src="${logo('safescan.png')}" class="print-logo" />
        </div>
      </div>
      <hr style="border:none;border-top:1px solid #e0e0e0;margin:6px 0 12px 0" />
      <h2 class="print-title">Batch Pick List</h2>
      <table class="print-table">
        <thead>
          <tr>
            <th class="col-stock">Stock Item</th>
            <th class="col-prescription">Prescription ID</th>
            <th class="col-qty">Qty</th>
            <th class="col-packs">Packs</th>
            <th class="col-picked">Picked Y/N</th>
          </tr>
        </thead>
        <tbody>${rows}
        </tbody>
      </table>
    </div>
  </body>
</html>`;
  }

  /**
   * Opens the window the pick list will print from. Call it in the click handler itself, before
   * any request: pop-up blockers allow a window only while the click is being handled.
   */
  openWindow(): Window | null {
    return window.open('', '_blank', 'width=800,height=600');
  }

  /** Writes the pick list into a window from openWindow(); it prints and closes itself. */
  write(popup: Window, batchData: any): void {
    popup.document.open();
    popup.document.write(this.pickListHtml(batchData, new Date().toLocaleDateString('en-GB'), document.baseURI));
    popup.document.close();
  }

  /** Opens the pick list in a new window, which prints and closes itself. False if the popup was blocked. */
  print(batchData: any): boolean {
    const popup = this.openWindow();
    if (!popup) return false;
    this.write(popup, batchData);
    return true;
  }
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
